import { createApp } from 'vue';
import App from './App';
import { assign } from 'lodash';
import { ConfigurationService } from '@nsftx/games-config';
import * as packageTranslations from '@nsftx/i18n-next-six';
import gamesStatic from '@nsftx/games-sdk-js/src/api/gamesStatic';
import createTranslationsProxy from '@nsftx/games-sdk-js/src/utility/translationsProxy';
import sentry from '@nsftx/games-sdk-js/src/utility/sentry';
import { webViewIntegration } from './utility';
import router from './router';
import store from './store';

const qp = new URLSearchParams(window.location.search);
const appName = qp.get('application') || 'web';
const tenantId = qp.get('tenantId');
const messageProtocol = qp.get('messageProtocol') || qp.get('integrationType') || 'postMessage';
const deliveryPlatform = qp.get('deliveryPlatform') || qp.get('application') || 'Web';
const token = qp.get('token');
const playerId = qp.get('id') || qp.get('playerId') || qp.get('player');
const clientType = qp.get('authStrategy') || qp.get('clientType');
const deployment = window.location.hostname.includes('nsoft.app') ? 'cf' : 'netlify';
const platform = qp.get('platform');
let productUuid = null;
const customDomain = qp.get('customDomain');
const isNextGen = platform === 'NextGen';
const parent = qp.get('parent');
const nextGenStandaloneIntegration = typeof parent !== 'undefined' && parent === 'false';
let productName = qp.get('productName');
const productNamePrefixes = ['SRMV', 'NVNV', 'NSNV'];
for (const productPrefix of productNamePrefixes) {
  if (productName.startsWith(productPrefix)) productName = productName.slice(productPrefix.length);
}

window.addEventListener('message', (event) => {
  if (event.data.action === 'Slave.Load') {
    productUuid = event.data.data.account.uuid;
  }
});

if (isNextGen) {
  window.addEventListener('message', (event) => {
    if (event.data.action === 'User.AuthorizationChanged') {
      const authData = event.data.data.auth;
      const loggedIn = !!authData.token;
      authData.user.logged = loggedIn;
      store.dispatch('setUserBalance', authData.user.balance);
      store.dispatch('user/updateUser', {
        auth: authData,
        profile: authData.user,
      });
    }
    if (event.data.action === 'User.BalanceChanged') {
      store.dispatch('setUserBalance', event.data.data.balance);
    }
  });

  window.addEventListener('Configuration:SetConfiguration', (event) => {
    store.dispatch('setNextGenConfig', event.detail);
  });
}

(async () => {
  const clientAdapters = [];
  const nextGenAdapterData = {
    name: 'IgniteAdapter',
    options: {
      allowedOrigins: '*',
      displayId: 'nextsix',
      version: '1.1',
      providerId: process.env.VUE_APP_ENVIRONMENT === 'production' ? 30 : 26,
      environment: process.env.VUE_APP_ENVIRONMENT,
    },
  };
  const gravityGatewayAdapterData = {
    name: 'GravityGatewayAdapter',
    options: {
      slaveId: 'PluginNextSix',
    },
  };
  if (isNextGen) {
    clientAdapters.push(nextGenAdapterData);
  } else {
    clientAdapters.push(gravityGatewayAdapterData);
  }
  const configSettings = {
    environment: process.env.NODE_ENV,
    requiredPaths: [],
    applicationName: appName,
    productName: productName || 'NextSix',
    channel: deliveryPlatform,
    messageProtocol,
    clientAdapters,
    ui: tenantId,
    deployment,
    customDomain,
  };
  const usePackageTranslations = store.state.usePackageTranslations;
  if (!usePackageTranslations) configSettings.translationDomains = ['web', 'mobile', 'ngs'];
  const configService = new ConfigurationService(configSettings);
  const config = await configService.getConfiguration();

  if (usePackageTranslations) {
    // NPM PACKED TRANS
    const translations = await packageTranslations.exportTranslations(config.locale, {
      companyName: config.tenantName || config.rawData?.companyInfo?.name,
      companyUuid: tenantId,
      env: process.env.NODE_ENV,
    });
    const proxyTranslations = createTranslationsProxy(translations, ['nextsix_']);
    await store.dispatch('setTranslations', proxyTranslations);
  } else {
    // STANDARD STATIC + HTTP CONFIG TRANS
    const translations = await gamesStatic.getTranslations(config.locale);
    await store.dispatch('setTranslations', assign(config.translations, translations));
  }
  await store.dispatch('setConfig', config);
  if (isNextGen) {
    await store.dispatch('updateConfigWithNextGenConfig');
    store.dispatch('setNextGenStandaloneIntegration', nextGenStandaloneIntegration);
  }
  await store.dispatch('setDeviceLayout');
  store.state.config.channel = 'web';
  config.customDomain = customDomain;
  const isPerBet = config.paymentStrategy[deliveryPlatform.toLowerCase()].value === 'perBet';
  const paymentStrategy = {
    paymentStrategy: config.paymentStrategy,
  };
  const mergedRules = { ...config.rules, ...paymentStrategy };
  const paymentChips = config.ui?.config?.paymentChips;
  const betslipConfig = {
    betLayout: 'E',
    channel: config.applicationName,
    ticketTypes: ['single'],
    rules: mergedRules,
    taxes: config.taxes,
    isPossibleWinActive: true,
    isPaymentBetAllowed: isPerBet,
    isPaymentPerBetAllowed: isPerBet,
    isFuturePerBetAllowed: false,
    isFuturePerTicketAllowed: false,
    isTicketCancelAllowed: false,
    isTicketRebetAllowed: false,
    minIncrement: 0.01,
    futureRounds: 1,
    paymentChips,
  };
  await store.dispatch('setRules', mergedRules);
  await store.dispatch('gamesBetslip/setConfig', betslipConfig);
  store.state.config.productUuid = productUuid;
  if (playerId) {
    store.state.config.playerId = playerId;
  }
  if (token && config.ui.config.mode === 'standalone') {
    store.state.config.platformConfig.clientType = clientType;
    store.state.config.platformConfig.playerId = playerId;
    await store.dispatch('user/setUserSessionToken', token);
    await store.dispatch('getPlayer', { config, token });
  }

  const app = createApp(App, {
    extends: App,
    beforeCreate() {
      if (webViewIntegration.isActive()) {
        window.WebAppListener.sendMessage('Slave.Init');
      }
    },
  });

  if (process.env.VUE_APP_SENTRY_ACTIVE === 'true') {
    sentry.start({
      app,
      dsn: process.env.VUE_APP_SENTRY_DSN,
      sampleRate: 0.01,
      trackComponents: true,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Network Error',
        'timeout of 5000ms exceeded',
        'timeout of 10000ms exceeded',
        'Android is not defined',
        /.*NotAllowedError.*/gm,
        /.*WakeLock.*/gm,
      ],
    });
  }

  app.config.productionTip = false;
  app.use(router).use(store).mount('#app');
})();
